import { Tab } from '@/core/collections/general/tab/tab.interface';
import { checkAccess } from '@/core/helpers/auth.helper';
import store from '@/core/store';
import i18n from '@/i18n';
import { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

//redirects to the first tab the user has access to, based on the journey scopes
async function redirectToFirstAccessibleTab(to: RouteLocationNormalized) {
   if (to.name !== 'journey-details') return;

   let journey = store.getters.journey;
   if (!journey || journey.id != to.params.journeyId) {
      journey = await store.dispatch('getSetJourney', { id: to.params.journeyId });
   }

   const tabs = (to.meta.tabs as Array<Tab>) || [];
   const accessibleTab = tabs.find((tab) => checkAccess(tab.scope || 'view', journey?.scopes));

   if (accessibleTab) {
      return { name: accessibleTab.route_name, params: { ...to.params, ...accessibleTab.params }, query: to.query };
   }

   return {
      path: '/error',
      query: {
         error_code: '403',
         error_title: i18n.global.t('access-denied'),
         error_message: i18n.global.t('u-have-no-access'),
         error_action: 'home-or-logout',
      },
   };
}

const journeyRoutes: RouteRecordRaw[] = [
   {
      path: '',
      name: 'journey-overview',
      components: {
         default: () => import('@/modules/journeys/pages/Overview.vue'),
      },
      beforeEnter: (to: any) => {
         if ('page' in to.query) return;
         else return { name: 'journey-overview', query: { page: 1 } };
      },
      meta: {
         permission: 'journeys',
      },
      children: [
         {
            name: 'journey-overview-add',
            path: 'create',
            meta: {
               permission: 'journeys',
               scopes: ['create'],
            },
            components: {
               create_modal: () => import('@/modules/journeys/components/CreateModal.vue'),
            },
         },
      ],
   },
   {
      path: ':journeyId',
      name: 'journey-details',
      component: () => import('@/modules/journeys/pages/JourneyDetail.vue'),
      beforeEnter: redirectToFirstAccessibleTab,
      meta: {
         permission: 'journeys',
         tabs: [
            {
               route_name: 'journey-result',
               title_key: 'result',
               scope: 'submission',
            },
            {
               route_name: 'journey-edit-page',
               params: {
                  step: 1,
                  page: 1,
               },
               title_key: 'edit',
               scope: 'update',
            },
            {
               route_name: 'journey-preview',
               title_key: 'preview',
               scope: 'view',
            },
            {
               route_name: 'journey-referrals',
               title_key: 'referral_codes',
               scope: 'view',
            },
            {
               route_name: 'journey-dashboard',
               title_key: 'dashboard',
               scope: 'dashboard',
            },
         ],
      },
      children: [
         {
            path: 'edit',
            name: 'journey-edit',
            component: () => import('@/modules/journeys/pages/Edit.vue'),
            meta: {
               permission: 'journeys',
               journey_scopes: ['update'],
            },
         },
         {
            path: 'dashboard',
            name: 'journey-dashboard',
            component: () => import('@/modules/journeys/pages/Dashboard.vue'),
            meta: {
               permission: 'journeys',
               journey_scopes: ['dashboard'],
            },
         },
         {
            path: 'referrals',
            name: 'journey-referrals',
            component: () => import('@/modules/journeys/pages/Referrals.vue'),
            meta: {
               permission: 'journeys',
            },
         },
         {
            path: 'step/:step',
            name: 'journey-edit-step',
            component: () => import('@/modules/journeys/pages/Edit.vue'),
            redirect: (from: any) => {
               return {
                  name: 'journey-edit-page',
                  params: {
                     journeyId: from.params.journeyId,
                     step: from.params.step || 1,
                     page: 1,
                  },
               };
            },

            meta: {
               permission: 'journeys',
               journey_scopes: ['update'],
            },
            children: [
               {
                  name: 'journey-edit-page',
                  path: 'page/:page',
                  meta: {
                     permission: 'journeys',
                     journey_scopes: ['update'],
                  },
                  children: [
                     {
                        name: 'journey-edit-page-admin',
                        path: 'admin',
                        meta: {
                           permission: 'journeys',
                           journey_scopes: ['update'],
                        },
                     },
                  ],
               },
            ],
         },
         {
            name: 'journey-step-settings',
            path: 'step/:step/page/:page/stepsettings/:setting?/:texttype?',
            component: () => import('@/modules/journeys/pages/StepSettings.vue'),
            meta: {
               permission: 'journeys',
               journey_scopes: ['update'],
            },
         },
         {
            path: 'result',
            name: 'journey-result',
            component: () => import('@/modules/journeys/pages/Result.vue'),
            props: {},
            meta: {
               permission: 'journeys',
               journey_scopes: ['submission'],
            },
            children: [
               {
                  name: 'journey-result-history',
                  path: 'history/:submissionId',
                  meta: {
                     permission: 'journeys',
                     journey_scopes: ['submission'],
                  },
               },
               {
                  name: 'journey-result-comment',
                  path: 'comment/:submissionId',
                  meta: {
                     permission: 'journeys',
                     journey_scopes: ['submission'],
                  },
               },
               {
                  path: '*',
                  redirect: '/journey/:journeyId/result',
                  meta: {
                     permission: 'journeys',
                     journey_scopes: ['submission'],
                  },
               },
            ],
         },
         {
            name: 'journey-preview',
            path: 'preview',
            component: () => import('@/modules/journeys/pages/Preview.vue'),
            query: { step: undefined, page: undefined, module: undefined },
            meta: {
               permission: 'journeys',
            },
         },
         {
            path: 'settings/:setting?/:texttype?',
            name: 'journey-settings',
            component: () => import('@/modules/journeys/pages/Settings.vue'),
            beforeEnter: [
               (to: any) => {
                  if (to.params['setting'] !== '') return;
                  return { name: 'journey-settings', params: { journeyId: to.params.journeyId, setting: 'general' } };
               },
            ],
            meta: {
               permission: 'journeys',
               journey_scopes: ['update'],
            },
         },
      ],
   },
];
export default journeyRoutes;
